import { gql } from '@apollo/client'
import { Caption } from '@farol-ds/react'
import { PaymentIconMethod } from '@jusbrasil-web/shared-payment-icon'
import { PaymentMethodButton } from '@jusbrasil-web/shared-payment-method-button'
import classNames from 'classnames'
import { useContext } from 'react'

import { useRegisterLockEvent, useSendOneClickButtonEvents } from '../../hooks'
import { type UseSendOneClickButtonEvents_ViewerFragment } from '../../hooks/__generated__/use-send-one-click-button-events.graphql'
import { BoxLockContext } from '../context'
import { SubscribeButton, SubscribeButtonProps } from '../subscribe-button'
import { CheckoutButton_OfferLockCardFragment } from './__generated__/checkout-button.graphql'
import styles from './checkout-button.module.scss'

export type CheckoutButtonProps = {
  offerLockCard: CheckoutButton_OfferLockCardFragment
  viewer?: UseSendOneClickButtonEvents_ViewerFragment | null
  kind?: SubscribeButtonProps['kind']
  leftIcon?: SubscribeButtonProps['leftIcon']
  width?: 'full' | 'auto'
  forceDisabled?: boolean
  forceCheckout?: boolean
  customLabel?: string
}

export const CheckoutButton = ({
  offerLockCard,
  viewer,
  kind,
  leftIcon,
  width,
  forceDisabled,
  forceCheckout = false,
  customLabel,
}: CheckoutButtonProps) => {
  const context = useContext(BoxLockContext)
  const { interactive, setStatus } = context
  const { registerLockEvent } = useRegisterLockEvent()
  const { metricsRef, sendChangeCardEvent } = useSendOneClickButtonEvents({
    offerLockCard,
    viewer,
  })

  const {
    creditCard,
    submitData: { jwt, submitUrl },
  } = offerLockCard
  const hasCreditCard = !!creditCard?.ccToken
  const showCreditCard = hasCreditCard && !forceCheckout

  const getSubmitUrl = () => {
    const baseURL = new URL(submitUrl)
    baseURL.searchParams.set('cameFrom', offerLockCard.lockEventProperties.from_component || '')

    return baseURL.toString()
  }

  const handleSubmit = (event: React.FormEvent) => {
    const isEmbeddedModal = context.componentType === 'modal' && context.embeddedCheckoutEnabled

    setStatus('processing')

    registerLockEvent({
      eventProperties: [offerLockCard.lockEventProperties],
    })

    if (isEmbeddedModal) {
      event.preventDefault()
      context.openEmbeddedCheckout(getSubmitUrl(), { jwt })
    }

    sendChangeCardEvent()
  }

  const formClassName = classNames(styles.form, {
    [styles.formWidthAuto]: width === 'auto',
  })

  const cardWithCustomLabel = customLabel
    ? {
        ...offerLockCard,
        button: {
          ...offerLockCard.button,
          label: customLabel,
        },
      }
    : offerLockCard

  return (
    <form method="post" action={getSubmitUrl()} onSubmit={handleSubmit} className={formClassName}>
      <input type="hidden" name="jwt" value={jwt} />

      {showCreditCard ? (
        <div className={styles['form-wrapper-credit-card']}>
          <Caption>Pagar com:</Caption>

          <PaymentMethodButton
            ref={metricsRef}
            type="submit"
            label="Cartão de Crédito"
            helpText={`Final ${creditCard.lastDigits}`}
            method={creditCard.brandName as PaymentIconMethod}
            disabled={!interactive || forceDisabled}
          />
        </div>
      ) : (
        <SubscribeButton
          offerLockCard={cardWithCustomLabel}
          kind={kind}
          leftIcon={leftIcon}
          forceDisabled={forceDisabled}
        />
      )}
    </form>
  )
}

CheckoutButton.fragments = {
  offerLockCard: gql`
    fragment CheckoutButton_offerLockCard on OfferLockCard {
      submitData {
        jwt
        submitUrl
      }
      creditCard {
        ccToken
        lastDigits
        brandName
      }
      lockEventProperties {
        pid
        feature
        current_plan
        next_plan
        plan_price
        modal_type
        experiment_id
        app_id
        from_component
        resource_id
        parent_resource_id
        is_upgrade
        is_one_click
      }

      ...SubscribeButton_offerLockCard
      ...UseSendOneClickButtonEvents_offerLockCard
    }

    ${SubscribeButton.fragments.offerLockCard}
    ${useSendOneClickButtonEvents.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment CheckoutButton_viewer on Profile {
      ...UseSendOneClickButtonEvents_viewer
    }

    ${useSendOneClickButtonEvents.fragments.viewer}
  `,
}
